"use client";

import { cn } from "@/lib/utils";

export interface PlanToggleOption<T extends string> {
  value: T;
  label: string;
}

interface PlanToggleProps<T extends string> {
  label: string;
  options: PlanToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

/** Pill-shaped segmented control used to pick a plan flavour. */
const PlanToggle = <T extends string>({
  label,
  options,
  value,
  onChange,
}: PlanToggleProps<T>) => {
  // Three labels need to breathe more than two inside the same pill
  const dense = options.length > 2;

  return (
    <div
      role="group"
      aria-label={label}
      className="flex gap-1 rounded-full bg-muted p-1"
    >
      {options.map((option) => {
        const active = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex-1 rounded-full py-2 transition-colors duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
              dense ? "px-1 text-[12.5px]" : "px-2 text-[13px]",
              active
                ? "bg-primary font-bold text-primary-foreground"
                : "bg-transparent font-semibold text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default PlanToggle;
